// Normalize any CSS color a computed style can hand us into six-digit hex.
//
// Payment iframes are cross-origin and don't inherit our variables, so their
// appearance APIs take concrete strings — and narrow ones. Blackbaud documents
// six-digit hex only; Stripe documents HEX / rgb() / hsl(). What we read is
// neither: Tailwind 4 ships its palette as oklch(), our brand ramp derives
// through color-mix(), and a partner's configured brand color reaches
// --color-brand as whatever they typed, including three-digit shorthand that
// custom properties pass through unnormalized. One conversion at the boundary
// covers all of those.
//
// Round-trip through a 1x1 canvas to force sRGB rasterization. Reading
// fillStyle back isn't enough — Chrome preserves the original oklch string when
// the color is well-defined in a wide gamut — so we paint the pixel and read it
// through getImageData, which makes the browser commit to concrete RGBA bytes.
const byte = n => n.toString(16).padStart(2, '0')

const cssColorToHex = (() => {
  let ctx
  return value => {
    // undefined means "not tried yet"; null means tried and unavailable, which
    // is the jsdom case under vitest (no canvas package). Returning '' there
    // matches what getCssVar already yields in that environment.
    if (ctx === undefined) {
      ctx = document.createElement('canvas').getContext('2d', { willReadFrequently: true })
    }
    if (!ctx) return ''

    ctx.clearRect(0, 0, 1, 1)
    // Canvas silently ignores an unparseable fillStyle and keeps the previous
    // one, which would return the last color we converted rather than an error.
    // Priming with transparent turns that into a=0, i.e. the empty result below.
    ctx.fillStyle = 'rgba(0, 0, 0, 0)'
    ctx.fillStyle = value
    ctx.fillRect(0, 0, 1, 1)

    const [ r, g, b, a ] = ctx.getImageData(0, 0, 1, 1).data
    // Empty for unparseable or fully transparent input. Callers pass it straight
    // through and the iframe falls back to its own default, which is why the
    // appearance modules carry no hardcoded fallbacks of their own.
    if (a === 0) return ''
    // Eight digits when the source was translucent. Nothing we read is today,
    // and Blackbaud documents six — but silently flattening alpha against an
    // assumed backdrop would be a worse way to discover that.
    return a === 255
      ? `#${byte(r)}${byte(g)}${byte(b)}`
      : `#${byte(r)}${byte(g)}${byte(b)}${byte(a)}`
  }
})()

export default cssColorToHex
